import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { array, fn, hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { Button, Chip } from 'tio-ui/components/buttons';
import { eq, gte } from 'tio-ui/utilities';
// tio-reimagined components (27 total)
import ActionableCard from 'tio-reimagined/components/actionable-card';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import CircularProgress from 'tio-reimagined/components/circular-progress';
import Drawer from 'tio-reimagined/components/drawer';
import EmptyState from 'tio-reimagined/components/empty-state';
import FaqAccordion from 'tio-reimagined/components/faq-accordion';
import FilterTabs from 'tio-reimagined/components/filter-tabs';
import GlassCard from 'tio-reimagined/components/glass-card';
import HighlightCard from 'tio-reimagined/components/highlight-card';
import IconBadge from 'tio-reimagined/components/icon-badge';
import InfoRow from 'tio-reimagined/components/info-row';
import MatchScoreBadge from 'tio-reimagined/components/match-score-badge';
import NavCard from 'tio-reimagined/components/nav-card';
import PageHero from 'tio-reimagined/components/page-hero';
import PipelineFlow from 'tio-reimagined/components/pipeline-flow';
import ProgressBar from 'tio-reimagined/components/progress-bar';
import ProgressCircle from 'tio-reimagined/components/progress-circle';
import SectionHeader from 'tio-reimagined/components/section-header';
import SectionPanel from 'tio-reimagined/components/section-panel';
import SkillChip from 'tio-reimagined/components/skill-chip';
import StatCard from 'tio-reimagined/components/stat-card';
import StatusBadge from 'tio-reimagined/components/status-badge';
import StepCard from 'tio-reimagined/components/step-card';
import TaEligibilityBadge from 'tio-reimagined/components/ta-eligibility-badge';
import Timeline from 'tio-reimagined/components/timeline';
import TimelineTracker from 'tio-reimagined/components/timeline-tracker';
import {
  PhArrowLeft,
  PhArrowRight,
  PhBriefcase,
  PhCheckCircle,
  PhCircleNotch,
  PhClock,
  PhCode,
  PhCube,
  PhCurrencyDollar,
  PhFileSearch,
  PhGraduationCap,
  PhGridFour,
  PhHeart,
  PhInfo,
  PhLayout,
  PhListDashes,
  PhLightning,
  PhMapTrifold,
  PhPiggyBank,
  PhRocket,
  PhSidebar,
  PhSparkle,
  PhStar,
  PhTag,
  PhTarget,
  PhTrendUp,
  PhUsers,
  PhWarning,
} from 'ember-phosphor-icons';

// ---------------------------------------------------------------------------
// Demo data
// ---------------------------------------------------------------------------

const SAMPLE_TABS = [
  { id: 'all', label: 'All', count: 24 },
  { id: 'recommended', label: 'Recommended', count: 8 },
  { id: 'in-progress', label: 'In Progress', count: 3 },
  { id: 'completed', label: 'Completed', count: 5 },
];

const SAMPLE_SKILLS = [
  { name: 'Leadership', level: 85, category: 'Management' },
  { name: 'Patient Care', level: 95, category: 'Clinical' },
  { name: 'Epic EMR', level: 72, category: 'Technical' },
  { name: 'Budget Management', level: 45, category: 'Management' },
];

const SAMPLE_FAQ = [
  {
    question: 'What is tuition assistance?',
    answer:
      'Tuition assistance is a benefit that helps cover the cost of education and training programs.',
  },
  {
    question: 'How do I apply?',
    answer: 'Navigate to the TAS section and submit an application with your program details.',
  },
  {
    question: 'What programs are eligible?',
    answer:
      'Eligible programs include degree programs, certifications, and approved professional development courses.',
  },
];

const SAMPLE_PIPELINE = [
  { value: 124, label: 'Applied', color: 'violet' as const },
  { value: 89, label: 'Reviewed', color: 'teal' as const },
  { value: 42, label: 'Approved', color: 'green' as const },
  { value: 8, label: 'Completed', color: 'amber' as const },
];

const SAMPLE_TIMELINE = [
  {
    title: 'Application Submitted',
    description: 'Jan 15, 2026',
    state: 'completed' as const,
    date: '2026-01-15',
  },
  {
    title: 'Documents Reviewed',
    description: 'Jan 22, 2026',
    state: 'completed' as const,
    date: '2026-01-22',
  },
  { title: 'Approval Pending', description: 'In review by manager', state: 'active' as const },
  { title: 'Payment Processed', state: 'upcoming' as const },
];

// ---------------------------------------------------------------------------
// Showcase component
// ---------------------------------------------------------------------------

export default class ComponentShowcase extends Component {
  @tracked activeTab = 'all';
  @tracked isDrawerOpen = false;
  @tracked drawerPlacement: 'left' | 'right' | 'top' | 'bottom' = 'right';
  @tracked drawerSize: 'sm' | 'md' | 'lg' | 'xl' | 'full' = 'md';

  setActiveTab = (tabId: string) => {
    this.activeTab = tabId;
  };

  openDrawer = (
    placement: 'left' | 'right' | 'top' | 'bottom',
    size: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  ) => {
    this.drawerPlacement = placement;
    this.drawerSize = size;
    this.isDrawerOpen = true;
  };

  closeDrawer = () => {
    this.isDrawerOpen = false;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Back navigation }}
      <div class="mb-4">
        <LinkTo
          @route="authenticated.reimagined"
          class="inline-flex items-center gap-2 text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 font-medium"
        >
          <PhArrowLeft @size={{18}} />
          Back to Reimagined
        </LinkTo>
      </div>

      {{! Header }}
      <PageHero
        @title="Component Library"
        @subtitle="27 shared components in tio-reimagined. Battle-tested and ready for cross-app reuse."
        @icon={{PhCube}}
        @theme="gray"
        @decoration="dots"
      >
        <:actions>
          <Chip @intent="primary">
            <PhCode @size={{14}} class="mr-1" />
            27 Components
          </Chip>
          <Chip @intent="success">
            <PhCheckCircle @size={{14}} class="mr-1" />
            Fully Tested
          </Chip>
        </:actions>
      </PageHero>

      <div class="space-y-8">

        {{! ================================================================
            CATEGORY: Layout & Navigation
            ================================================================ }}
        <Heading
          @level={{2}}
          class="text-gray-700 dark:text-gray-200 flex items-center gap-2 border-b dark:border-gray-700 pb-2"
        >
          <PhLayout @size={{22}} class="text-violet-600" />
          Layout &amp; Navigation
        </Heading>

        {{! ========================================
            PageHero
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhLayout @size={{20}} class="text-violet-600" />
              <span>PageHero</span>
              <Chip @size="sm" @intent="primary">11 Themes</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Gradient hero banner with two modes:
              <strong>block mode</strong>
              (icon/badge/footer blocks) and
              <strong>convenience mode</strong>
              (@title/@subtitle/@icon with stat/actions/content blocks). 11 theme presets, 4 SVG
              decoration patterns, customizable rounding.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4 space-y-4">
              {{! Convenience mode demo }}
              <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Convenience Mode:</Text>
              <PageHero
                @title="Section 127 Optimizer"
                @subtitle="Maximize your tax-free education benefit"
                @icon={{PhPiggyBank}}
                @theme="emerald"
                @decoration="circles"
              >
                <:stat>
                  <div class="text-3xl font-bold">$5,250</div>
                  <div class="text-sm text-white/70">Annual Limit</div>
                </:stat>
                <:actions>
                  <Button
                    @intent="default"
                    @appearance="outlined"
                    @size="sm"
                    class="text-white border-white/40 hover:bg-white/10"
                  >
                    Learn More
                  </Button>
                </:actions>
              </PageHero>

              {{! Block mode demo }}
              <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Block Mode:</Text>
              <PageHero @theme="violet" @decoration="circles">
                <:icon>
                  <PhGraduationCap @size={{32}} />
                  <Heading @level={{2}} class="text-white">Violet + Circles</Heading>
                </:icon>
                <:badge>
                  <Chip @intent="primary">Badge Slot</Chip>
                </:badge>
                <:default>
                  <Text class="text-white/80">Block mode with icon, badge, and footer blocks.</Text>
                </:default>
                <:footer>
                  <Text class="text-white/70 text-sm">Footer block with top border divider</Text>
                </:footer>
              </PageHero>

              {{! Theme grid }}
              <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">All 11 Themes:</Text>
              <div class="grid grid-cols-3 lg:grid-cols-4 gap-3">
                <PageHero @theme="violet" @rounded="lg"><span
                    class="font-semibold text-white text-sm"
                  >Violet</span></PageHero>
                <PageHero @theme="emerald" @rounded="lg"><span
                    class="font-semibold text-white text-sm"
                  >Emerald</span></PageHero>
                <PageHero @theme="blue" @rounded="lg"><span
                    class="font-semibold text-white text-sm"
                  >Blue</span></PageHero>
                <PageHero @theme="amber" @rounded="lg"><span
                    class="font-semibold text-white text-sm"
                  >Amber</span></PageHero>
                <PageHero @theme="teal" @rounded="lg"><span
                    class="font-semibold text-white text-sm"
                  >Teal</span></PageHero>
                <PageHero @theme="indigo" @rounded="lg"><span
                    class="font-semibold text-white text-sm"
                  >Indigo</span></PageHero>
                <PageHero @theme="sky" @rounded="lg"><span
                    class="font-semibold text-white text-sm"
                  >Sky</span></PageHero>
                <PageHero @theme="rose" @rounded="lg"><span
                    class="font-semibold text-white text-sm"
                  >Rose</span></PageHero>
                <PageHero @theme="red" @rounded="lg"><span
                    class="font-semibold text-white text-sm"
                  >Red</span></PageHero>
                <PageHero @theme="gray" @rounded="lg"><span
                    class="font-semibold text-white text-sm"
                  >Gray</span></PageHero>
                <PageHero @theme="fuchsia" @rounded="lg"><span
                    class="font-semibold text-white text-sm"
                  >Fuchsia</span></PageHero>
              </div>
            </div>

            {{! Code example accordion }}
            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>{{! Convenience mode }}
                  &lt;PageHero @title="Section 127 Optimizer" @subtitle="Maximize your tax-free
                  benefit" @icon=&#123;&#123;PhPiggyBank&#125;&#125; @theme="emerald"
                  @decoration="circles" &gt; &lt;:stat&gt; &lt;div class="text-3xl
                  font-bold"&gt;$5,250&lt;/div&gt; &lt;/:stat&gt; &lt;:actions&gt;
                  &lt;Button&gt;Learn More&lt;/Button&gt; &lt;/:actions&gt; &lt;/PageHero&gt;

                  {{! Block mode }}
                  &lt;PageHero @theme="violet" @decoration="circles"&gt; &lt;:icon&gt;
                  &lt;PhGraduationCap @size=&#123;&#123;32&#125;&#125; /&gt; &lt;Heading
                  @level=&#123;&#123;1&#125;&#125; class="text-white"&gt;Title&lt;/Heading&gt;
                  &lt;/:icon&gt; &lt;:default&gt;Body content&lt;/:default&gt; &lt;:footer&gt;Footer
                  content&lt;/:footer&gt; &lt;/PageHero&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ========================================
            NavCard
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhMapTrifold @size={{20}} class="text-violet-600" />
              <span>NavCard</span>
              <Chip @size="sm" @intent="success">3 Layouts</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Unified navigation card with vertical, horizontal, and gradient layout variants.
              Consolidates the former QuickActionLink, ActionLinkCard, and ToolCard components.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4 space-y-4">
              <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Vertical
                (default):</Text>
              <div class="grid grid-cols-3 gap-4">
                <NavCard
                  @route="authenticated.reimagined"
                  @icon={{PhRocket}}
                  @title="Get Started"
                  @subtitle="Begin your journey"
                  @color="violet"
                />
                <NavCard
                  @route="authenticated.reimagined"
                  @icon={{PhTarget}}
                  @title="Explore"
                  @subtitle="Find programs"
                  @color="teal"
                />
                <NavCard
                  @route="authenticated.reimagined"
                  @icon={{PhStar}}
                  @title="Favorites"
                  @subtitle="Saved items"
                  @color="amber"
                />
              </div>

              <Text
                class="font-semibold text-gray-700 dark:text-gray-300 text-sm"
              >Horizontal:</Text>
              <div class="space-y-2">
                <NavCard
                  @route="authenticated.reimagined"
                  @icon={{PhBriefcase}}
                  @title="Career Explorer"
                  @subtitle="Browse career paths"
                  @layout="horizontal"
                  @color="indigo"
                />
                <NavCard
                  @route="authenticated.reimagined"
                  @icon={{PhGraduationCap}}
                  @title="Programs"
                  @subtitle="View eligible programs"
                  @layout="horizontal"
                  @color="emerald"
                />
              </div>

              <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Gradient:</Text>
              <div class="grid grid-cols-2 gap-4">
                <NavCard
                  @route="authenticated.reimagined"
                  @icon={{PhLightning}}
                  @title="Skills Assessment"
                  @subtitle="Evaluate your skills"
                  @layout="gradient"
                  @color="violet"
                />
                <NavCard
                  @route="authenticated.reimagined"
                  @icon={{PhTrendUp}}
                  @title="Career Growth"
                  @subtitle="Track progress"
                  @layout="gradient"
                  @color="teal"
                />
              </div>
            </div>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>&lt;NavCard @route="authenticated.reimagined"
                  @icon=&#123;&#123;PhRocket&#125;&#125; @title="Get Started" @subtitle="Begin your
                  journey" @color="violet" /&gt; &lt;NavCard @route="authenticated.reimagined"
                  @icon=&#123;&#123;PhBriefcase&#125;&#125; @title="Career Explorer"
                  @layout="horizontal" @color="indigo" /&gt; &lt;NavCard
                  @route="authenticated.reimagined" @icon=&#123;&#123;PhLightning&#125;&#125;
                  @title="Skills Assessment" @layout="gradient" @color="violet" /&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ========================================
            SectionHeader + SectionPanel
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhGridFour @size={{20}} class="text-violet-600" />
              <span>SectionHeader &amp; SectionPanel</span>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              <strong>SectionHeader</strong>
              — icon + title heading with optional subtitle and trailing slot.
              <strong>SectionPanel</strong>
              — full card container with icon header and body.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4 space-y-4">
              <SectionHeader
                @icon={{PhStar}}
                @title="Section Header"
                @subtitle="With subtitle and trailing content"
              >
                <:trailing>
                  <Chip @intent="primary" @size="sm">Badge</Chip>
                </:trailing>
              </SectionHeader>

              <SectionPanel @icon={{PhBriefcase}} @title="Section Panel" @iconColor="violet">
                <:trailing>
                  <Button @intent="primary" @size="sm" @appearance="outlined">Action</Button>
                </:trailing>
                <:body>
                  <Text @intent="muted">Panel body content goes here. This is a card container with
                    a styled header.</Text>
                </:body>
              </SectionPanel>
            </div>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>&lt;SectionHeader @icon=&#123;&#123;PhStar&#125;&#125; @title="My Section"
                  @subtitle="Optional subtitle" &gt;
                  &lt;:trailing&gt;&lt;Chip&gt;Badge&lt;/Chip&gt;&lt;/:trailing&gt;
                  &lt;/SectionHeader&gt; &lt;SectionPanel @icon=&#123;&#123;PhBriefcase&#125;&#125;
                  @title="Panel Title" @iconColor="violet" &gt; &lt;:body&gt;Content
                  here&lt;/:body&gt; &lt;/SectionPanel&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ========================================
            Drawer
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhSidebar @size={{20}} class="text-violet-600" />
              <span>Drawer</span>
              <Chip @size="sm" @intent="primary">Overlay</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Slide-out panel overlay with backdrop, keyboard (Escape) support,
              close-on-outside-click, and named blocks for header, body, and footer. 4 placements, 5
              sizes.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
              <div class="flex flex-wrap gap-2">
                <Button
                  @intent="primary"
                  @appearance="outlined"
                  @size="sm"
                  {{on "click" (fn this.openDrawer "right" "md")}}
                >
                  Right (md)
                </Button>
                <Button
                  @intent="primary"
                  @appearance="outlined"
                  @size="sm"
                  {{on "click" (fn this.openDrawer "right" "lg")}}
                >
                  Right (lg)
                </Button>
                <Button
                  @intent="primary"
                  @appearance="outlined"
                  @size="sm"
                  {{on "click" (fn this.openDrawer "left" "md")}}
                >
                  Left (md)
                </Button>
                <Button
                  @intent="primary"
                  @appearance="outlined"
                  @size="sm"
                  {{on "click" (fn this.openDrawer "bottom" "md")}}
                >
                  Bottom (md)
                </Button>
                <Button
                  @intent="primary"
                  @appearance="outlined"
                  @size="sm"
                  {{on "click" (fn this.openDrawer "top" "sm")}}
                >
                  Top (sm)
                </Button>
              </div>
            </div>

            <Drawer
              @isOpen={{this.isDrawerOpen}}
              @onClose={{this.closeDrawer}}
              @placement={{this.drawerPlacement}}
              @size={{this.drawerSize}}
            >
              <:header>
                <Heading @level={{3}}>Sample Drawer</Heading>
              </:header>
              <:body>
                <div class="space-y-4">
                  <Text>
                    Slides in from the
                    <strong>{{this.drawerPlacement}}</strong>
                    at size
                    <strong>{{this.drawerSize}}</strong>.
                  </Text>
                  <Text @intent="muted">Press Escape, click backdrop, or use the close button.</Text>
                </div>
              </:body>
              <:footer>
                <div class="flex justify-end">
                  <Button @intent="primary" {{on "click" this.closeDrawer}}>Close</Button>
                </div>
              </:footer>
            </Drawer>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>&lt;Drawer @isOpen=&#123;&#123;this.isOpen&#125;&#125;
                  @onClose=&#123;&#123;this.close&#125;&#125; @placement="right" @size="md" &gt;
                  &lt;:header&gt;&lt;Heading
                  @level=&#123;&#123;3&#125;&#125;&gt;Title&lt;/Heading&gt;&lt;/:header&gt;
                  &lt;:body&gt;Content&lt;/:body&gt; &lt;:footer&gt;Footer&lt;/:footer&gt;
                  &lt;/Drawer&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ================================================================
            CATEGORY: Data Display
            ================================================================ }}
        <Heading
          @level={{2}}
          class="text-gray-700 dark:text-gray-200 flex items-center gap-2 border-b dark:border-gray-700 pb-2 mt-4"
        >
          <PhTrendUp @size={{22}} class="text-violet-600" />
          Data Display
        </Heading>

        {{! ========================================
            StatCard (updated)
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhTrendUp @size={{20}} class="text-violet-600" />
              <span>StatCard</span>
              <Chip @size="sm" @intent="primary">2 Modes</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Metric display with icon, value, label, and optional trend. Supports
              <strong>default</strong>
              mode (large card with icon circle) and
              <strong>compact</strong>
              mode (smaller, inline icon — formerly SummaryStatCard).
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4 space-y-4">
              <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Default Mode:</Text>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  @icon={{PhCurrencyDollar}}
                  @value="$5,250"
                  @label="TA Available"
                  @trend="up"
                  @trendText="Renewed"
                  @color="green"
                />
                <StatCard
                  @icon={{PhCheckCircle}}
                  @value="68"
                  @suffix="%"
                  @label="Plan Progress"
                  @trend="up"
                  @trendText="+12% this month"
                  @color="violet"
                />
                <StatCard @icon={{PhBriefcase}} @value="8" @label="Open Roles" @color="blue" />
                <StatCard
                  @label="Rating"
                  @value="4.5"
                  @suffix="/5"
                  @icon={{PhStar}}
                  @valueColor="amber"
                  @color="amber"
                />
              </div>

              <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Compact Mode:</Text>
              <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
                <StatCard
                  @compact={{true}}
                  @label="In Pipeline"
                  @value="124"
                  @icon={{PhUsers}}
                  @iconColor="violet"
                  @valueColor="violet"
                />
                <StatCard
                  @compact={{true}}
                  @label="Approved"
                  @value="42"
                  @trend="up"
                  @trendText="+8% QoQ"
                  @valueColor="emerald"
                />
                <StatCard
                  @compact={{true}}
                  @label="Budget Used"
                  @value="85"
                  @suffix="%"
                  @secondaryText="of annual allocation"
                  @valueColor="amber"
                />
              </div>
            </div>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>{{! Default mode }}
                  &lt;StatCard @icon=&#123;&#123;PhCurrencyDollar&#125;&#125; @value="$5,250"
                  @label="TA Available" @trend="up" @trendText="Renewed" @color="green" /&gt;

                  {{! Compact mode (formerly SummaryStatCard) }}
                  &lt;StatCard @compact=&#123;&#123;true&#125;&#125; @label="In Pipeline"
                  @value="124" @icon=&#123;&#123;PhUsers&#125;&#125; @iconColor="violet"
                  @valueColor="violet" /&gt;

                  {{! With suffix and secondary text }}
                  &lt;StatCard @compact=&#123;&#123;true&#125;&#125; @label="Budget Used"
                  @value="85" @suffix="%" @secondaryText="of annual allocation" /&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ========================================
            ProgressBar + CircularProgress
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhTarget @size={{20}} class="text-violet-600" />
              <span>ProgressBar &amp; CircularProgress</span>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              <strong>ProgressBar</strong>
              — horizontal bar with ARIA, 9 colors, 4 sizes, gradient option.
              <strong>CircularProgress</strong>
              — SVG ring with content slot.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4 space-y-4">
              <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Progress Bars:</Text>
              <div class="space-y-3">
                <ProgressBar @value={{75}} @color="emerald" @size="md" />
                <ProgressBar @value={{50}} @color="violet" @size="sm" />
                <ProgressBar @value={{30}} @color="amber" @size="xs" @gradient={{true}} />
              </div>

              <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Circular
                Progress:</Text>
              <div class="flex items-end gap-8">
                <CircularProgress @value={{92}} @color="emerald" @size="lg">
                  <span class="text-lg font-bold text-emerald-600 dark:text-emerald-400">92%</span>
                </CircularProgress>
                <CircularProgress @value={{65}} @color="violet" @size="md">
                  <span class="text-sm font-bold text-violet-600 dark:text-violet-400">65%</span>
                </CircularProgress>
                <CircularProgress @value={{30}} @color="amber" @size="sm">
                  <span class="text-xs font-bold text-amber-600 dark:text-amber-400">30%</span>
                </CircularProgress>
              </div>
            </div>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>&lt;ProgressBar @value=&#123;&#123;75&#125;&#125; @color="emerald" @size="md"
                  /&gt; &lt;ProgressBar @value=&#123;&#123;50&#125;&#125;
                  @gradient=&#123;&#123;true&#125;&#125; /&gt; &lt;CircularProgress
                  @value=&#123;&#123;92&#125;&#125; @color="emerald" @size="lg"&gt; &lt;span
                  class="text-lg font-bold"&gt;92%&lt;/span&gt; &lt;/CircularProgress&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ========================================
            PipelineFlow
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhArrowRight @size={{20}} class="text-violet-600" />
              <span>PipelineFlow</span>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Horizontal flow visualization with stages connected by arrows. Uses PipelineStage
              internally. Supports equal-width and wrapping modes.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
              <PipelineFlow @stages={{SAMPLE_PIPELINE}} @size="md" />
            </div>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>const stages = [ &#123; value: 124, label: 'Applied', color: 'violet' &#125;,
                  &#123; value: 89, label: 'Reviewed', color: 'teal' &#125;, &#123; value: 42,
                  label: 'Approved', color: 'green' &#125;, ]; &lt;PipelineFlow
                  @stages=&#123;&#123;stages&#125;&#125; @size="md" /&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ========================================
            TimelineTracker
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhClock @size={{20}} class="text-violet-600" />
              <span>TimelineTracker</span>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Vertical timeline with completed/active/upcoming state markers connected by lines.
              Supports compact mode for tighter spacing.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
              <div class="max-w-md">
                <TimelineTracker @steps={{SAMPLE_TIMELINE}} />
              </div>
            </div>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>const steps = [ &#123; title: 'Submitted', state: 'completed', date:
                  '2026-01-15' &#125;, &#123; title: 'In Review', description: 'Manager review',
                  state: 'active' &#125;, &#123; title: 'Approved', state: 'upcoming' &#125;, ];
                  &lt;TimelineTracker @steps=&#123;&#123;steps&#125;&#125; /&gt; &lt;TimelineTracker
                  @steps=&#123;&#123;steps&#125;&#125; @compact=&#123;&#123;true&#125;&#125; /&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ========================================
            InfoRow
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhInfo @size={{20}} class="text-violet-600" />
              <span>InfoRow</span>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Simple label/value row for detail panels and cards. Supports block fallback for rich
              value content.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
              <div class="divide-y dark:divide-gray-700">
                <InfoRow @label="Program" @value="MBA Healthcare Administration" />
                <InfoRow @label="Institution" @value="University of Phoenix" />
                <InfoRow @label="Status">
                  <Chip @intent="success" @size="sm">Active</Chip>
                </InfoRow>
                <InfoRow @label="Amount" @value="$2,400" @size="sm" />
              </div>
            </div>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>&lt;InfoRow @label="Program" @value="MBA Healthcare" /&gt;

                  {{! Block fallback for rich content }}
                  &lt;InfoRow @label="Status"&gt; &lt;Chip @intent="success"&gt;Active&lt;/Chip&gt;
                  &lt;/InfoRow&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ================================================================
            CATEGORY: Cards
            ================================================================ }}
        <Heading
          @level={{2}}
          class="text-gray-700 dark:text-gray-200 flex items-center gap-2 border-b dark:border-gray-700 pb-2 mt-4"
        >
          <PhGridFour @size={{22}} class="text-violet-600" />
          Cards
        </Heading>

        {{! ========================================
            ActionableCard
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhLayout @size={{20}} class="text-violet-600" />
              <span>ActionableCard</span>
              <Chip @size="sm" @intent="primary">Complex</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Card with header badge, match score, metadata row, skill chips, eligibility, and
              actions. Used for courses, certifications, and job listings.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
              <ActionableCard
                @title="Nurse Leadership Certificate"
                @matchScore={{98}}
                @showBanner={{true}}
                @bannerText="Top Pick for You"
                class="max-w-2xl"
              >
                <:badge><Chip @intent="success">Best Match</Chip></:badge>
                <:metadata>
                  <div
                    class="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-400"
                  >
                    <span class="flex items-center gap-1"><PhGraduationCap
                        @size={{16}}
                      />ANCC</span>
                    <span class="flex items-center gap-1"><PhClock @size={{16}} />4 months</span>
                    <span class="flex items-center gap-1"><PhCurrencyDollar
                        @size={{16}}
                      />$1,850</span>
                  </div>
                </:metadata>
                <:skills>
                  <Chip @size="sm">Leadership</Chip>
                  <Chip @size="sm">Team Management</Chip>
                  <Chip @size="sm">Quality Improvement</Chip>
                </:skills>
                <:eligibility>
                  <TaEligibilityBadge @eligible={{true}} @amount="$1,850 covered" />
                </:eligibility>
                <:actions>
                  <button
                    type="button"
                    class="flex items-center gap-1 text-gray-400 dark:text-gray-500 hover:text-pink-500 dark:hover:text-pink-400 transition-colors"
                  >
                    <PhHeart @size={{20}} /><span class="text-sm">Save</span>
                  </button>
                  <div class="flex gap-2">
                    <Button @intent="default" @appearance="outlined" @size="sm">Learn More</Button>
                    <Button
                      @intent="primary"
                      @size="sm"
                      @icon={{PhArrowRight}}
                      @label="Enroll Now"
                    />
                  </div>
                </:actions>
              </ActionableCard>
            </div>
          </:body>
        </Section>

        {{! ========================================
            HighlightCard
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhSparkle @size={{20}} class="text-violet-600" />
              <span>HighlightCard</span>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Gradient-background card for showcasing benefits or key features. Icon, title, value,
              description, with optional extra block.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <HighlightCard
                  @icon={{PhCurrencyDollar}}
                  @title="Annual Benefit"
                  @value="$5,250"
                  @description="Tax-free per year"
                  @color="emerald"
                />
                <HighlightCard
                  @icon={{PhGraduationCap}}
                  @title="Programs"
                  @value="12"
                  @description="Eligible options"
                  @color="violet"
                />
                <HighlightCard
                  @icon={{PhTrendUp}}
                  @title="Savings"
                  @value="$18K"
                  @description="Lifetime total"
                  @color="blue"
                />
                <HighlightCard
                  @icon={{PhCheckCircle}}
                  @title="Approval Rate"
                  @value="94%"
                  @description="This quarter"
                  @color="amber"
                />
              </div>
            </div>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>&lt;HighlightCard @icon=&#123;&#123;PhCurrencyDollar&#125;&#125; @title="Annual
                  Benefit" @value="$5,250" @description="Tax-free per year" @color="emerald" /&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ========================================
            GlassCard
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhSparkle @size={{20}} class="text-violet-600" />
              <span>GlassCard</span>
              <Chip @size="sm" @intent="success">For Dark BGs</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Translucent frosted-glass stat card designed for dark/hero background contexts. Pairs
              well with PageHero.
            </Text>

            <div class="bg-gradient-to-r from-violet-600 to-purple-700 rounded-lg p-6 mb-4">
              <div class="grid grid-cols-3 gap-4">
                <GlassCard
                  @icon={{PhCurrencyDollar}}
                  @label="Available"
                  @value="$5,250"
                  @subtext="Annual limit"
                  @accent="emerald"
                />
                <GlassCard
                  @icon={{PhCheckCircle}}
                  @label="Used"
                  @value="$2,100"
                  @subtext="This year"
                  @accent="white"
                />
                <GlassCard
                  @icon={{PhTrendUp}}
                  @label="Savings"
                  @value="$18K"
                  @subtext="Lifetime"
                  @accent="amber"
                />
              </div>
            </div>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>{{! Place inside a PageHero or dark gradient }}
                  &lt;GlassCard @icon=&#123;&#123;PhCurrencyDollar&#125;&#125; @label="Available"
                  @value="$5,250" @subtext="Annual limit" @accent="emerald" /&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ========================================
            StepCard
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhCheckCircle @size={{20}} class="text-violet-600" />
              <span>StepCard</span>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Onboarding/progress step card with numbered indicator. Supports completed and active
              states.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4 space-y-3">
              <StepCard
                @step={{1}}
                @title="Create Account"
                @description="Set up your profile and preferences"
                @completed={{true}}
              >
                <:action>
                  <Chip @intent="success" @size="sm">Done</Chip>
                </:action>
              </StepCard>
              <StepCard
                @step={{2}}
                @title="Submit Application"
                @description="Choose a program and apply for TA"
                @active={{true}}
              >
                <:action>
                  <Button @intent="primary" @size="sm">Start</Button>
                </:action>
              </StepCard>
              <StepCard
                @step={{3}}
                @title="Track Progress"
                @description="Monitor your application status"
              />
            </div>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>&lt;StepCard @step=&#123;&#123;1&#125;&#125; @title="Create Account"
                  @description="Set up your profile" @completed=&#123;&#123;true&#125;&#125; &gt;
                  &lt;:action&gt;&lt;Chip @intent="success"&gt;Done&lt;/Chip&gt;&lt;/:action&gt;
                  &lt;/StepCard&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ================================================================
            CATEGORY: Feedback & Status
            ================================================================ }}
        <Heading
          @level={{2}}
          class="text-gray-700 dark:text-gray-200 flex items-center gap-2 border-b dark:border-gray-700 pb-2 mt-4"
        >
          <PhWarning @size={{22}} class="text-violet-600" />
          Feedback &amp; Status
        </Heading>

        {{! ========================================
            AlertBanner
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhWarning @size={{20}} class="text-violet-600" />
              <span>AlertBanner</span>
              <Chip @size="sm" @intent="primary">4 Intents</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Contextual alert/notice banner with automatic icon mapping. Supports warning, info,
              success, and tip intents with optional title and action block.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4 space-y-3">
              <AlertBanner @intent="warning" @title="Action Required">
                Your application deadline is approaching. Please submit by Feb 15.
              </AlertBanner>
              <AlertBanner @intent="info">
                Your TA benefit renews on January 1st each year.
              </AlertBanner>
              <AlertBanner @intent="success" @title="Application Approved">
                Your tuition assistance has been approved for $2,400.
              </AlertBanner>
              <AlertBanner @intent="tip">
                <:default>
                  Pro tip: You can apply for multiple programs in the same benefit year.
                </:default>
                <:action>
                  <Button @intent="primary" @size="sm" @appearance="outlined">Learn More</Button>
                </:action>
              </AlertBanner>
            </div>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>&lt;AlertBanner @intent="warning" @title="Action Required"&gt; Deadline is
                  approaching. &lt;/AlertBanner&gt; &lt;AlertBanner @intent="tip"&gt; Pro tip: Apply
                  for multiple programs. &lt;:action&gt; &lt;Button @size="sm"&gt;Learn
                  More&lt;/Button&gt; &lt;/:action&gt; &lt;/AlertBanner&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ========================================
            EmptyState
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhFileSearch @size={{20}} class="text-violet-600" />
              <span>EmptyState</span>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Placeholder for empty lists or data sections. Icon, title, description, and optional
              action. Supports compact mode for inline use.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4 space-y-4">
              <EmptyState
                @icon={{PhFileSearch}}
                @title="No applications yet"
                @description="Start by exploring eligible programs and submitting your first application."
                @color="violet"
              >
                <:action>
                  <Button @intent="primary" @size="sm">Browse Programs</Button>
                </:action>
              </EmptyState>

              <Text class="font-semibold text-gray-700 dark:text-gray-300 text-sm">Compact:</Text>
              <EmptyState
                @icon={{PhStar}}
                @title="No favorites saved"
                @compact={{true}}
                @color="gray"
              />
            </div>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>&lt;EmptyState @icon=&#123;&#123;PhFileSearch&#125;&#125; @title="No
                  applications yet" @description="Start exploring programs." @color="violet" &gt;
                  &lt;:action&gt; &lt;Button&gt;Browse Programs&lt;/Button&gt; &lt;/:action&gt;
                  &lt;/EmptyState&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ========================================
            IconBadge
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhSparkle @size={{20}} class="text-violet-600" />
              <span>IconBadge</span>
              <Chip @size="sm" @intent="success">11 Colors</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Colored circular icon badge. Building block for lists, cards, and section headers.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
              <div class="flex items-end gap-4">
                <div class="text-center">
                  <IconBadge @icon={{PhStar}} @color="violet" @size="lg" />
                  <Text @size="xs" @intent="muted" class="mt-1">lg</Text>
                </div>
                <div class="text-center">
                  <IconBadge @icon={{PhCheckCircle}} @color="emerald" @size="md" />
                  <Text @size="xs" @intent="muted" class="mt-1">md</Text>
                </div>
                <div class="text-center">
                  <IconBadge @icon={{PhClock}} @color="amber" @size="sm" />
                  <Text @size="xs" @intent="muted" class="mt-1">sm</Text>
                </div>
                <div class="text-center">
                  <IconBadge @icon={{PhTrendUp}} @color="blue" @size="md" />
                  <Text @size="xs" @intent="muted" class="mt-1">blue</Text>
                </div>
                <div class="text-center">
                  <IconBadge @icon={{PhHeart}} @color="pink" @size="md" />
                  <Text @size="xs" @intent="muted" class="mt-1">pink</Text>
                </div>
                <div class="text-center">
                  <IconBadge @icon={{PhWarning}} @color="red" @size="md" />
                  <Text @size="xs" @intent="muted" class="mt-1">red</Text>
                </div>
              </div>
            </div>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>&lt;IconBadge @icon=&#123;&#123;PhStar&#125;&#125; @color="violet" @size="lg"
                  /&gt; &lt;IconBadge @icon=&#123;&#123;PhCheckCircle&#125;&#125; @color="emerald"
                  @size="md" /&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ========================================
            FaqAccordion
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhInfo @size={{20}} class="text-violet-600" />
              <span>FaqAccordion</span>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Expandable FAQ list. One item open at a time. Accepts an array of question/answer
              pairs.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
              <FaqAccordion @items={{SAMPLE_FAQ}} />
            </div>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>const items = [ &#123; question: 'What is TA?', answer: 'Tuition assistance...'
                  &#125;, &#123; question: 'How do I apply?', answer: 'Navigate to...' &#125;, ];
                  &lt;FaqAccordion @items=&#123;&#123;items&#125;&#125; /&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ================================================================
            CATEGORY: Patterns
            ================================================================ }}
        <Heading
          @level={{2}}
          class="text-gray-700 dark:text-gray-200 flex items-center gap-2 border-b dark:border-gray-700 pb-2 mt-4"
        >
          <PhCode @size={{22}} class="text-violet-600" />
          Patterns
        </Heading>

        {{! ========================================
            FilterTabs
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhGridFour @size={{20}} class="text-violet-600" />
              <span>FilterTabs</span>
              <Chip @size="sm" @intent="primary">tio-reimagined</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Horizontal pill-style filter buttons with active state and optional count badges.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
              <FilterTabs
                @tabs={{SAMPLE_TABS}}
                @activeId={{this.activeTab}}
                @onSelect={{this.setActiveTab}}
              />
            </div>

            <details class="border dark:border-gray-700 rounded-lg">
              <summary
                class="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Code Examples
              </summary>
              <pre
                class="p-4 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 overflow-x-auto border-t dark:border-gray-700"
              ><code>const tabs = [ &#123; id: 'all', label: 'All', count: 24 &#125;, &#123; id:
                  'active', label: 'Active', count: 8 &#125;, ]; &lt;FilterTabs
                  @tabs=&#123;&#123;tabs&#125;&#125;
                  @activeId=&#123;&#123;this.activeTab&#125;&#125;
                  @onSelect=&#123;&#123;this.setActiveTab&#125;&#125; /&gt;</code></pre>
            </details>
          </:body>
        </Section>

        {{! ========================================
            MatchScoreBadge
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhTarget @size={{20}} class="text-violet-600" />
              <span>MatchScoreBadge</span>
              <Chip @size="sm" @intent="primary">tio-reimagined</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Circular percentage display with automatic color coding based on thresholds.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
              <div class="flex items-end gap-8">
                {{#each (array 95 78 55 32) as |score|}}
                  <div class="text-center">
                    <MatchScoreBadge @score={{score}} @size="lg" />
                    <Text @intent="muted" @size="xs" class="mt-2">
                      {{#if (gte score 90)}}Excellent{{else if (gte score 70)}}Good{{else if
                        (gte score 50)
                      }}Fair{{else}}Low{{/if}}
                    </Text>
                  </div>
                {{/each}}
              </div>
            </div>
          </:body>
        </Section>

        {{! ========================================
            SkillChip
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhSparkle @size={{20}} class="text-violet-600" />
              <span>SkillChip</span>
              <Chip @size="sm" @intent="primary">tio-reimagined</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Skill tag with optional level indicator bar.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4 space-y-4">
              <div class="flex flex-wrap gap-3">
                {{#each SAMPLE_SKILLS as |skill|}}
                  <SkillChip @name={{skill.name}} @level={{skill.level}} />
                {{/each}}
              </div>
              <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
                <Text @size="sm" @intent="muted" class="mb-2">Without level:</Text>
                <div class="flex flex-wrap gap-2">
                  <SkillChip @name="Leadership" @intent="primary" @showLevel={{false}} />
                  <SkillChip @name="Patient Care" @intent="success" @showLevel={{false}} />
                  <SkillChip @name="Epic EMR" @intent="default" @showLevel={{false}} />
                </div>
              </div>
            </div>
          </:body>
        </Section>

        {{! ========================================
            TaEligibilityBadge
            ======================================== }}
        <Section class="bg-white dark:bg-gray-900 rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhGraduationCap @size={{20}} class="text-violet-600" />
              <span>TaEligibilityBadge</span>
              <Chip @size="sm" @intent="primary">tio-reimagined</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Tuition Assistance eligibility indicator with optional amount and partial coverage.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
              <div class="flex flex-wrap gap-4">
                <TaEligibilityBadge @eligible={{true}} @amount="$2,400 covered" />
                <TaEligibilityBadge @eligible={{true}} />
                <TaEligibilityBadge
                  @eligible={{true}}
                  @partial={{true}}
                  @partialAmount="$1,200"
                  @totalAmount="$2,400"
                />
                <TaEligibilityBadge @eligible={{false}} />
              </div>
            </div>
          </:body>
        </Section>

        {{! ================================================================
            ProgressCircle
            ================================================================ }}
        <Section
          class="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden"
        >
          <:header>
            <div class="flex items-center gap-3 flex-wrap">
              <div
                class="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center"
              >
                <PhCircleNotch @size={{18}} class="text-indigo-600 dark:text-indigo-400" />
              </div>
              <Heading
                @level={{3}}
                class="text-gray-900 dark:text-gray-100"
              >ProgressCircle</Heading>
              <Chip @intent="primary" @size="sm">SVG</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              SVG circular progress indicator with configurable size, colors, stroke width, and
              optional center label.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
              <div class="flex flex-wrap items-end gap-8">
                <div class="text-center">
                  <ProgressCircle @progress={{75}} @size="sm" />
                  <Text @size="sm" @intent="muted" class="mt-2">Small (75%)</Text>
                </div>
                <div class="text-center">
                  <ProgressCircle @progress={{42}} @size="md" @progressColor="#059669" />
                  <Text @size="sm" @intent="muted" class="mt-2">Medium (42%)</Text>
                </div>
                <div class="text-center">
                  <ProgressCircle
                    @progress={{90}}
                    @size="lg"
                    @progressColor="#7c3aed"
                    @sublabel="Complete"
                  />
                  <Text @size="sm" @intent="muted" class="mt-2">Large (90%)</Text>
                </div>
                <div class="text-center">
                  <ProgressCircle
                    @progress={{100}}
                    @size="md"
                    @progressColor="#059669"
                    @label="Done"
                  />
                  <Text @size="sm" @intent="muted" class="mt-2">Custom label</Text>
                </div>
              </div>
            </div>
          </:body>
        </Section>

        {{! ================================================================
            StatusBadge
            ================================================================ }}
        <Section
          class="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden"
        >
          <:header>
            <div class="flex items-center gap-3 flex-wrap">
              <div
                class="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center"
              >
                <PhTag @size={{18}} class="text-green-600 dark:text-green-400" />
              </div>
              <Heading @level={{3}} class="text-gray-900 dark:text-gray-100">StatusBadge</Heading>
              <Chip @intent="primary" @size="sm">Semantic</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Semantic status chip that auto-maps status strings to intents (completed → success,
              in-progress → primary, planned → default). Wraps tio-ui Chip.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
              <div class="flex flex-wrap gap-3">
                <StatusBadge @status="completed" />
                <StatusBadge @status="in-progress" />
                <StatusBadge @status="planned" />
                <StatusBadge @label="Custom Label" @intent="warning" />
                <StatusBadge @label="Error" @intent="danger" />
              </div>
            </div>
          </:body>
        </Section>

        {{! ================================================================
            Timeline
            ================================================================ }}
        <Section
          class="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden"
        >
          <:header>
            <div class="flex items-center gap-3 flex-wrap">
              <div
                class="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900 flex items-center justify-center"
              >
                <PhListDashes @size={{18}} class="text-violet-600 dark:text-violet-400" />
              </div>
              <Heading @level={{3}} class="text-gray-900 dark:text-gray-100">Timeline</Heading>
              <Chip @intent="primary" @size="sm">Layout</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Vertical timeline with named blocks for custom nodes and content. Supports any item
              type via generics.
            </Text>

            <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-4">
              <Timeline
                @items={{array
                  (hash title="Application Submitted" date="Jan 15" status="completed")
                  (hash title="Under Review" date="Feb 1" status="in-progress")
                  (hash title="Approval Pending" date="—" status="planned")
                }}
              >
                <:node as |item|>
                  <div
                    class="w-4 h-4 rounded-full
                      {{if
                        (eq item.status 'completed')
                        'bg-green-500'
                        (if (eq item.status 'in-progress') 'bg-blue-500' 'bg-gray-300')
                      }}"
                  ></div>
                </:node>
                <:content as |item|>
                  <div class="pb-4">
                    <Text class="font-medium text-gray-900 dark:text-gray-100">{{item.title}}</Text>
                    <Text @size="sm" @intent="muted">{{item.date}}</Text>
                  </div>
                </:content>
              </Timeline>
            </div>
          </:body>
        </Section>

        {{! ================================================================
            Summary
            ================================================================ }}
        <div
          class="bg-violet-50 dark:bg-violet-950 rounded-xl p-6 border border-violet-100 dark:border-violet-800"
        >
          <div class="flex items-start gap-4">
            <div
              class="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0"
            >
              <PhCode @size={{24}} class="text-violet-600" />
            </div>
            <div>
              <Heading @level={{3}} class="text-violet-900 dark:text-violet-100 mb-2">Component
                Library Summary</Heading>
              <Text @intent="muted" class="mb-4">
                27 shared components in tio-reimagined, fully tested and ready for cross-app reuse
                in tio-employee, tio-admin, and tio-agent.
              </Text>
              <Text @size="sm" @intent="muted" class="mb-2">tio-reimagined (27):</Text>
              <div class="flex flex-wrap gap-2 mb-3">
                <Chip @intent="success" @size="sm">ActionableCard</Chip>
                <Chip @intent="success" @size="sm">AlertBanner</Chip>
                <Chip @intent="success" @size="sm">CircularProgress</Chip>
                <Chip @intent="success" @size="sm">Drawer</Chip>
                <Chip @intent="success" @size="sm">EmptyState</Chip>
                <Chip @intent="success" @size="sm">FaqAccordion</Chip>
                <Chip @intent="success" @size="sm">FilterTabs</Chip>
                <Chip @intent="success" @size="sm">GlassCard</Chip>
                <Chip @intent="success" @size="sm">HighlightCard</Chip>
                <Chip @intent="success" @size="sm">IconBadge</Chip>
                <Chip @intent="success" @size="sm">InfoRow</Chip>
                <Chip @intent="success" @size="sm">MatchScoreBadge</Chip>
                <Chip @intent="success" @size="sm">NavCard</Chip>
                <Chip @intent="success" @size="sm">PageHero</Chip>
                <Chip @intent="success" @size="sm">PipelineFlow</Chip>
                <Chip @intent="success" @size="sm">PipelineStage</Chip>
                <Chip @intent="success" @size="sm">ProgressBar</Chip>
                <Chip @intent="success" @size="sm">ProgressCircle</Chip>
                <Chip @intent="success" @size="sm">SectionHeader</Chip>
                <Chip @intent="success" @size="sm">SectionPanel</Chip>
                <Chip @intent="success" @size="sm">SkillChip</Chip>
                <Chip @intent="success" @size="sm">StatCard</Chip>
                <Chip @intent="success" @size="sm">StatusBadge</Chip>
                <Chip @intent="success" @size="sm">StepCard</Chip>
                <Chip @intent="success" @size="sm">TaEligibilityBadge</Chip>
                <Chip @intent="success" @size="sm">Timeline</Chip>
                <Chip @intent="success" @size="sm">TimelineTracker</Chip>
              </div>
            </div>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}
